import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = '';
const WS_URL = API_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const NotificationListener = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated || !WS_URL) return;

    const connect = () => {
      // تنظيف الاتصال السابق إن وجد
      if (wsRef.current) {
        wsRef.current.close();
      }
      
      console.log('🔔 Connecting to WebSocket for notifications...');
      const ws = new WebSocket(`${WS_URL}/api/ws/prices`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('🔔 WebSocket connected!');
        
        // إرسال ping كل 25 ثانية للحفاظ على الاتصال
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // تجاهل رسائل ping
          if (data.type === 'ping' || data.type === 'pong') {
            return;
          }
          
          console.log('🔔 Message received, type:', data.type);
          
          // إشعار من المدير
          if (data.type === 'notification') {
            console.log('🔔 ✅ Notification received:', data);
            const notification = {
              id: Date.now(),
              title: data.data?.title || 'إشعار',
              message: data.data?.message || '',
              type: data.data?.type || 'info'
            };
            
            setNotifications(prev => [notification, ...prev]);
            playNotificationSound();
            
            // إزالة الإشعار تلقائياً بعد 10 ثواني
            setTimeout(() => {
              setNotifications(prev => prev.filter(n => n.id !== notification.id));
            }, 10000);
          }
          
          // تسجيل خروج إجباري
          if (data.type === 'force_logout' && user && data.user_id === user.id) {
            console.log('🔔 Force logout received');
            alert(data.reason || 'تم تسجيل خروجك');
            logout();
            window.location.href = '/login';
          }
          
        } catch (e) {
          // تجاهل الأخطاء للرسائل غير JSON
        }
      };

      ws.onclose = (event) => {
        console.log('🔔 WebSocket disconnected, code:', event.code);
        
        // تنظيف ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // إعادة الاتصال بعد 3 ثواني فقط إذا لم يكن إغلاق طبيعي
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(connect, 3000);
        }
      };

      ws.onerror = (error) => {
        console.error('🔔 WebSocket error:', error);
      };
    };

    connect();

    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [isAuthenticated, user, logout]);

  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp2dm5qYmJmbnZ+hoaGhoJ+fn5+fn5+fnp6dnZycm5qamZmYmJiYmJiYmJiZmZqam5ucnZ2en5+goKGhoaKioqKioqKioaGgoJ+fnp6dnZycm5ubmpqZmZiYmJiYmJiYmZmZmpqbnJydnp6fn6ChoaGioqOjo6OjoqKioaGgnp6dnJybm5qamZmYmJiXl5eXl5eYmJmZmpucnJ2enp+goKGhoqKjo6OjoqKhoaCfnp2cm5qamZmYl5eXl5eXl5eXmJiZmpucnZ2en5+goKGhoqKjo6OjoqKhoaCfnp2cm5qZmZiXl5eWlpaWlpeXmJmam5ydnp6fn6ChoaGioqOjo6OioqGgn56dnJuamZiYl5eWlpaWlpaXl5iZmpucnZ6en5+goKGhoqKjo6OjoqKhoJ+enZybmpmYl5eWlpaWlpaWl5eYmZqbnJ2enp+foKChoaKio6OjoqKhoJ+enZybmpmYl5eWlpaWlpaWl5eYmZqbnJ2enp+foKChoaKio6Ojo6KioaCfnp2cm5qZmJeXlpaWlpaWlpeXmJmam5ydnp6fn6ChoaGioqOjo6KioaCfnp2cm5qZmJeXlpaWlpaWlpeXmJmam5ydnp6fn6ChoaGioqOjo6KioaCfnp2cm5qZmJeXlpaWlpaWlpeYmJmam5ydnp6fn6ChoaGio6OjoqKhoJ+enZybmpmYl5eWlpaWlpaWl5eYmZqbnJ2enp+foKChoaKio6OjoqKhoJ+enZybmpmYl5eWlpaWlpaWl5eYmZqbnJ2enp+foKChoaKio6OjoqKhoJ+enZybmpmYl5eWlpaWlpaWl5eYmZqbnJ2enp+foKChoaKio6OjoqKhoJ+enZybmpmYl5eWlpaWlpaWl5eYmZqbnJ2enp+foKChoaKio6OjoqKhoJ+enZybmpmYl5eWlpaWlpaWl5eYmZqbnJ2enp+foKChoaKio6OjoqKhoJ+enZybmpmYl5eWlpaWlpaWl5eYmZqbnJ2enp+foKChoaKio6OjoqKhoJ+enZybmpmYl5eWlpaVlZaWl5eYmZqbnJ2enp+foKChoaKio6OjoqKhoJ+enZybmpmYl5eWlpaVlZaWl5eYmZqbnJ2enp+foKChoaKio6OjoqKhoJ+enZybmpmYl5eWlpaVlZaWl5eYmZqbnJ2enp+foKChoaKio6Ojo6KhoJ+enZybmpmYl5eWlpaVlZaWl5eYmZqbnJ2enp+foKChoaKio6OjoqKhoJ+enZybmpk=');
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch (e) {
      // تجاهل الأخطاء
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-6 h-6 text-green-400" />;
      case 'warning':
        return <AlertTriangle className="w-6 h-6 text-yellow-400" />;
      default:
        return <Info className="w-6 h-6 text-blue-400" />;
    }
  };

  const getBgColor = (type) => {
    switch (type) {
      case 'success':
        return 'from-green-500/20 to-green-500/5 border-green-500/50';
      case 'warning':
        return 'from-yellow-500/20 to-yellow-500/5 border-yellow-500/50';
      default:
        return 'from-blue-500/20 to-blue-500/5 border-blue-500/50';
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="fixed top-20 left-4 right-4 sm:left-auto sm:right-4 z-50 flex flex-col gap-3 sm:w-96 pointer-events-none" dir="rtl">
      {/* 🧹 Clear-all button when 2+ notifications stack (fixes iPad Safari
          issue where tiny X buttons were hard to tap on stacked cards) */}
      {notifications.length >= 2 && (
        <button
          data-testid="notif-clear-all"
          onClick={() => setNotifications([])}
          className="pointer-events-auto self-end px-3 py-1 rounded-full bg-black/70 border border-white/20 text-white text-[11px] font-bold backdrop-blur-lg shadow-lg hover:bg-black/90 transition-colors"
        >
          إخفاء الكل ({notifications.length}) ✕
        </button>
      )}
      <AnimatePresence>
        {notifications.map((notification) => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, x: 100, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.9 }}
            onClick={() => removeNotification(notification.id)}
            role="button"
            tabIndex={0}
            className={`pointer-events-auto cursor-pointer bg-gradient-to-r ${getBgColor(notification.type)} border backdrop-blur-xl rounded-xl p-4 shadow-2xl active:scale-[0.98] transition-transform`}
            title="اضغط على أي مكان لإخفاء التنبيه"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {getIcon(notification.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-white font-bold text-sm truncate">
                    {notification.title}
                  </h4>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeNotification(notification.id);
                    }}
                    className="text-zinc-300 hover:text-white transition-colors flex-shrink-0 w-8 h-8 -mr-2 -mt-1 flex items-center justify-center rounded-full active:bg-white/10"
                    aria-label="إغلاق"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-zinc-300 text-sm mt-1 leading-relaxed">
                  {notification.message}
                </p>
              </div>
            </div>
            
            {/* Progress bar */}
            <motion.div
              initial={{ width: '100%' }}
              animate={{ width: '0%' }}
              transition={{ duration: 10, ease: 'linear' }}
              className="h-1 bg-white/20 rounded-full mt-3"
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default NotificationListener;
